import { useMemo } from 'react';
import { FunnelChart, Funnel, LabelList, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp } from 'lucide-react';
import { Card, ChartTooltip, EmptyState, formatEuro, formatZahl } from '@elbklar/ui';
import { type AnfrageStatus } from '@/lib/api';
import { ANFRAGE_STATUS_CHART } from '@/lib/anfrageStatus';

interface PipelineStage {
  status: AnfrageStatus;
  name: string;
  value: number;
  count: number;
}

interface PipelineChartProps {
  stages: PipelineStage[];
  totalValue: number;
}

export function PipelineChart({ stages, totalValue }: PipelineChartProps) {
  const chartData = useMemo(() => {
    return stages.map((stage) => ({
      ...stage,
      fill: ANFRAGE_STATUS_CHART[stage.status],
    }));
  }, [stages]);

  const totalDeals = useMemo(() => {
    return stages.reduce((sum, stage) => sum + stage.count, 0);
  }, [stages]);

  if (stages.length === 0) {
    return (
      <Card title="Pipeline" description="Aktive Anfragen nach Status" icon={TrendingUp}>
        <EmptyState
          icon={TrendingUp}
          title="Keine Pipeline-Daten"
          description="Aktive Anfragen erscheinen hier, sobald eine angelegt ist."
        />
      </Card>
    );
  }

  return (
    <Card
      title="Pipeline"
      description={`${totalDeals} aktive Anfragen`}
      icon={TrendingUp}
      actions={
        <div className="text-right">
          <div className="t-stat tabular">{formatEuro(totalValue)}</div>
          <div className="t-label text-muted-foreground">Gesamtwert</div>
        </div>
      }
    >
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <FunnelChart>
            <Tooltip
              content={
                <ChartTooltip<PipelineStage>
                  titel={(stufe) => stufe.name}
                  zeilen={(stufe) => [
                    { label: 'Anfragen', value: formatZahl(stufe.count) },
                    { label: 'Wert', value: formatEuro(stufe.value) },
                  ]}
                />
              }
            />
            <Funnel dataKey="value" data={chartData} isAnimationActive>
              <LabelList position="center" fill="var(--background)" stroke="none" dataKey="name" className="text-sm font-medium" />
            </Funnel>
          </FunnelChart>
        </ResponsiveContainer>
      </div>
      {/* Legend */}
      <div className="mt-4 grid grid-cols-2 gap-2">
        {chartData.map((stage) => (
          <div key={stage.name} className="flex items-center gap-2 t-small">
            <span className="size-2 rounded-full" style={{ backgroundColor: stage.fill }} />
            <span className="text-muted-foreground">{stage.name}</span>
            <span className="ml-auto font-medium tabular">{stage.count}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}
